import React, { useContext, useRef, useState, useEffect } from 'react';

import Button from 'components/button';
import Dropzone from 'components/dropzone';
import FilesList from 'components/files-list';
import InputContainer from 'components/input-container';
import Spacer from 'components/spacer';
import Switch from 'components/switch';
import Tooltip from 'components/tooltip';

import LocaleContext from 'contexts/locale-context';
import NotificationContext from 'contexts/notification-context';

import generateId from 'helpers/generate-id';
import canAddFiles from 'helpers/can-add-files';


import AddCircle from 'assets/img/add-circle.svg';
import CheckCircle from 'assets/img/check-circle.svg';
import Lock from 'assets/img/lock-filled.svg';
import Times from 'assets/img/times-plain.svg';
import UploadIcon from 'assets/img/upload.svg';

const dropHandler = (files, onDrop, translations, setNotification) => (ev) => {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();
    const proxyFiles = [];

    if (ev.dataTransfer.items) {
        // Use DataTransferItemList interface to access the file(s)
        for (let i = 0; i < ev.dataTransfer.items.length; i++) {
            // If dropped items aren't files, reject them
            if (ev.dataTransfer.items[i].kind === 'file') {
                const file = ev.dataTransfer.items[i].getAsFile();

                if (file.type) proxyFiles.push(file);
            }
        }
    } else {
        // Use DataTransfer interface to access the file(s)
        for (let i = 0; i < ev.dataTransfer.files.length; i++) {
            const file = ev.dataTransfer.files[i];

            if (file.type) proxyFiles.push(file);
        }
    }

    try {
      const filesToSave = canAddFiles(files, proxyFiles, translations);
    
      if (onDrop) {
        onDrop(filesToSave)
      }
    } catch(e) {
      setNotification({ autoCancel: true, message: e.message, type: 'warn' })
    }
}

const getInactiveText = (pageTranslations, onBrowse) => (
  <>
    <p className='home__dropzone--text'>
      <b>{pageTranslations.drag_and_drop}</b>
    </p>
    <p className='home__dropzone--text'>
      {pageTranslations.or}
    </p>
    <Button onClick={onBrowse}>
      {pageTranslations.browse_files}
    </Button>
  </>
);

const getActiveText = (pageTranslations) => (
  <p className='home__dropzone--text'>
    {pageTranslations.drop_here}
  </p>
);

const getDropzoneText = ({
  active,
  pageTranslations,
  fileInputRef
}) => (
  <>
    {
      !active ?
      getInactiveText(pageTranslations, () => fileInputRef.current.click())
      :
      getActiveText(pageTranslations)
    }
  </>
);

const onFileInputChange = (files, setFiles, translations, setNotification) => function (e) {
  const filesInput = e.target;

  e.preventDefault();
  if (filesInput.files && filesInput.files.length > 0) {
      try {
        const filesToAdd = canAddFiles(files, filesInput.files, translations);

        appendFiles(files, setFiles, filesToAdd);
      } catch(e) {
        if (e.message) {
          setNotification({ autoCancel: true, message: e.message, type: 'danger' });
        }
      }
  }
}

const appendFiles = (files, setFiles, filesToAdd) => {
  setFiles([...files, ...processFiles(filesToAdd)]);
}

const onRemoveFile = (files, setFiles) =>
  fileToRemove =>
  setFiles(files.filter(file => fileToRemove.id !== file.id));


const onClearFiles = (setFiles) => () => setFiles([]);

const onDrop = (currentFiles, setFiles) => files => {
  appendFiles(currentFiles, setFiles, files);
};

const processFiles = files => files.map(
  file => ({
    file: file,
    id: generateId()
  })
);

const processUpload = ({files, password, onUpload, reset}) => e => {
  e.preventDefault();

  onUpload(files, password || '');
  reset();
}

const onTextChange = setFunc => e => {
  setFunc(e.target.value);
}

const reset = (setPassword, setRPassword, setVerifiedPassword) => () => {
  setPassword('');
  setRPassword('');
  setVerifiedPassword('');
}

const Upload = ({ onUpload, files: _files, password: _password }) => {
  const [ files, setFiles ] = useState(_files);
  const [ verifiedPassword, setVerifiedPassword ] = useState(_password);
  const [ password, setPassword ] = useState('');
  const [ rpassword, setRPassword ] = useState('');
  const [ showAddPassword, setShowAddPassword ] = useState(false);
  const { locale, translations } = useContext(LocaleContext);
  const fileInputRef = useRef(null);
  const { setNotification } = useContext(NotificationContext);

  const jsTranslations = translations.js;
  const pageTranslations = translations.index;
  const commonTranslations = translations.common;

  const renderProps = {
    pageTranslations,
    translations,
    files,
    setFiles
  };

  useEffect(() => {
    fileInputRef.current.onchange = onFileInputChange(files, setFiles, translations, setNotification);
  }, [fileInputRef.current])

  const onCancelPassword = () => {
    setShowAddPassword(false);
  }
  
  const onOkPassword = () => {
    if (password !== rpassword) {
      return setNotification({ message: jsTranslations.password_not_matching, type: 'danger' })
    }

    setVerifiedPassword(password);
    setPassword('');
    setRPassword('');
    setShowAddPassword(false);
  };

  const getFilesBox = () => {  
    return (
      <Dropzone
        containerClass={'home__dropzone__files__container'}
        onDrop={dropHandler(files, onDrop(files, setFiles), translations, setNotification)}
      >
        {() => {
          return (
            <div
              className='home__upload__container'
              style={{
                borderColor: passwordEnabled ? 'var(--color-success-intent)': 'var(--border-color-primary)'
              }}
            >
              <div
                className={`home__upload__password ${showAddPassword ? 'home__upload__password--show' : ''}`}
              >
                <div className='home__upload__files-header'>
                  <div onClick={() => fileInputRef.current.click()}>
                    <img className='home__icon' src={Lock}/>
                    <h4>{pageTranslations.enable_password}</h4>
                  </div>
                  <img
                    alt='times icon'
                    className='clickable'
                    src={Times}
                    onClick={onCancelPassword}
                  />
                </div>
                <Spacer size='m'/>

                <InputContainer
                  label={pageTranslations.password}
                  required
                >
                  {({ class: className }) => {
                    return (
                      <input
                        onChange={onTextChange(setPassword)}
                        className={className}
                        required
                        placeholder={pageTranslations.password}
                        value={password}
                        type='password'
                      />
                    )
                  }}
                </InputContainer>
                <InputContainer
                  label={pageTranslations.r_password}
                  required
                >
                  {({ class: className }) => {
                    return (
                      <input
                        onChange={onTextChange(setRPassword)}
                        className={className}
                        required
                        placeholder={pageTranslations.r_password}
                        value={rpassword}
                        type='password'
                      />
                    )
                  }}
                </InputContainer>

                <Spacer size='s'/>

                <Button
                  disabled={!password || password !== rpassword}
                  onClick={onOkPassword}
                >
                  {pageTranslations.enable}
                </Button>
              </div>
              <div className='home__upload__files-header'>
                <div onClick={() => fileInputRef.current.click()}>
                  <img alt='add circle icon' className='home__icon' src={AddCircle}/>
                  <h4>{pageTranslations.add_files}</h4>
                </div>
                <Switch
                  checked={passwordEnabled}
                  onClick={() => {
                    if (verifiedPassword) {
                      setVerifiedPassword('');
                    } else {
                      setShowAddPassword(true)
                    }
                  }}
                  label={passwordEnabled ? pageTranslations.password_enabled : pageTranslations.password_disabled}
                />
              </div>
              <FilesList
                files={files}
                onClearFiles={onClearFiles(setFiles)}
                onRemoveFile={onRemoveFile(files, setFiles)}
              />
            </div>
    
          );
        }}
      </Dropzone>)
  }

  const onClickDropzone = () => {
    fileInputRef.current.onchange = onFileInputChange(files, setFiles, translations);

    if (!hasFiles) fileInputRef.current.click()
  };

  const getDropzoneBox = () => {
    return (
      <div className='home__dropzone-container'>
{/*        <Dropzone
          containerClass={`home__action-box${!hasFiles ? ' home__dropzone' : ''}`}
          containerHoverClass='home__dropzone--hover'
          onClick={onClickDropzone}
          onDrop={dropHandler(files, onDrop(files, setFiles), translations, setNotification)}
        >
          {({ active }) => {
            return (
              getDropzoneText({ active, ...renderProps, setNotification, fileInputRef })
            );
          }}
        </Dropzone>*/}
        <div className='home__dropzone-description'>
          <h1>
            {pageTranslations.suspension_msg}
          </h1>

          <Spacer size='xs'/>

          <p>
            {pageTranslations.suspension_desc}
          </p>

        </div>
      </div>
    );
  }

  const hasFiles = files.length > 0;

  const passwordEnabled = !!verifiedPassword;

  if (!hasFiles && verifiedPassword) setVerifiedPassword('');

  return (
    <>
      <input ref={fileInputRef} multiple style={{ visibility: 'hidden' }} type='file'/>

      <>
        {
          !hasFiles ?
          getDropzoneBox()
          :
          getFilesBox()
        }
      </>
      
      <Spacer size='xl'/>

      {hasFiles &&
        <form
          onSubmit={
            processUpload({
              files,
              password: verifiedPassword,
              onUpload,
              jsTranslations,
              allowPassword: passwordEnabled,
              setNotification,
              reset: reset(setPassword, setRPassword, setVerifiedPassword)}
            )}
        >
          <>
            <p>
              {pageTranslations.by_continuing} <a href={`/${locale}/privacy-policy`}>{commonTranslations.privacy_policy}</a> {pageTranslations.and} <a href={`/${locale}/terms-of-use`}>{pageTranslations.terms_of_use}</a>
            </p>

            <div className='home__button-container'>
              <Tooltip content={showAddPassword ? 'finish enabling password before upload' : ''}>
                <Button
                  disabled={showAddPassword}
                  type="submit"
                  icon={<img alt='upload icon' className='home__button__upload-icon' src={UploadIcon}/>}
                >
                  {pageTranslations.upload}
                </Button>
              </Tooltip>
            </div>
          </>
        </form>
      }
    </>
  );
}

Upload.defaultProps = {
  files: [],
  password: ''
}

export default Upload;